import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plug, Plus, RefreshCw, Trash2, CheckCircle2, XCircle, ShieldCheck, Info, X, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "@/components/workspace/Portal";

// This is the ATITI CONNECT admin panel — the connectivity engine's UI.
// The underlying channel-manager infrastructure is a trade secret and MUST
// never be surfaced to the host. Copy stays branded "OTA Connectivity".
const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40";
const PROVIDER_TAGLINE = "Atiti's own connectivity engine — one credential connects Booking.com, Airbnb, Agoda, MMT, Expedia and more.";

export default function IntegrationsTab({ pid, propertyName, onClose }) {
  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [category, setCategory] = useState(null);
  // Link-status: shows a persistent red banner if the host has an OTA
  // account connected but hasn't finished the channel wizard (missing
  // property_link or room-type mappings) — the exact failure mode that
  // led to Evan's demo booking not appearing in Channex Messages.
  const [linkStatus, setLinkStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, c, ls] = await Promise.all([
        api.get("/pms/integrations/providers"),
        api.get("/pms/integrations/accounts"),
        pid ? api.get(`/pms/integrations/properties/${pid}/category`) : Promise.resolve({ data: null }),
        pid ? api.get(`/pms/properties/${pid}/channex/link-status`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setProviders(p.data.providers || []);
      setAccounts(a.data.accounts || []);
      setCategory(c.data);
      setLinkStatus(ls.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const body = loading ? (
    <div className="h-64 flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={26} /></div>
  ) : (
    <div className="space-y-5" data-testid="integrations-tab">
      {linkStatus && linkStatus.account_connected && linkStatus.fully_mapped && linkStatus.apps_pending && (
        <div data-testid="channex-apps-pending" className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-5 py-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed flex-1">
            <p className="font-display font-bold text-amber-900 text-sm">
              One-time Channex setup — install Booking CRS + Messages apps
            </p>
            <p className="mt-1">
              Availability on this property is syncing fine, but <b>Booking CRS</b> and
              <b> Channex Messages</b> apps need to be installed via the Channex Applications tab
              (platform limit — app install is admin-session-only, can't be done via API).
              Click below → in the Applications tab, open each app → <b>Install → tick ALL
              properties</b> → Save. One click installs across every property.
            </p>
            <a href={linkStatus.channex_apps_url} target="_blank" rel="noopener noreferrer"
              data-testid="channex-apps-link"
              className="mt-3 inline-flex items-center gap-2 btn-brand rounded-full px-4 py-2 text-xs font-semibold">
              Open Channex Applications tab →
            </a>
            <p className="text-[10px] text-amber-700 mt-1.5">
              After installing, click "Re-check" below — the banner will disappear once Channex confirms both apps are active.
            </p>
            <button data-testid="channex-apps-recheck"
              onClick={() => autoProvision(pid, load)}
              className="mt-1.5 text-[11px] font-semibold text-amber-900 underline hover:text-amber-700">
              Re-check installation status
            </button>
          </div>
        </div>
      )}
      {linkStatus && linkStatus.account_connected && !linkStatus.fully_mapped && (
        <div data-testid="channex-link-warning" className="bg-rose-50 border-2 border-rose-300 rounded-2xl px-5 py-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="text-xs text-rose-900 leading-relaxed flex-1">
            <p className="font-display font-bold text-rose-900 text-sm">
              Bookings on this property are NOT syncing to your OTA channels
            </p>
            <p className="mt-1">
              OTA Connectivity is <b>connected</b> but the property/room-type mapping is
              incomplete — <b>{linkStatus.units_mapped}/{linkStatus.units_total}</b> units mapped
              {!linkStatus.property_linked && " · property not linked yet"}. Every reservation you create
              here saves locally but will <b>not</b> reach Booking.com, Airbnb or Agoda calendars until you finish the channel wizard below.
            </p>
            {linkStatus.per_unit?.length > 0 && linkStatus.units_mapped < linkStatus.units_total && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {linkStatus.per_unit.filter(u => !u.linked).map(u => (
                  <span key={u.unit_id} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200">
                    {u.unit_name || u.unit_id.slice(0, 6)} — unmapped
                  </span>
                ))}
              </div>
            )}
            <button data-testid="channex-auto-provision-btn"
              onClick={() => autoProvision(pid, load)}
              className="mt-3 btn-brand rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-2">
              <RefreshCw size={12} /> Auto-provision this property to OTA Connectivity
            </button>
            <p className="text-[10px] text-rose-700 mt-1.5">
              One-click: creates the Channex property, all room types, rate plans, subscribes the webhook and runs the initial 500-day sync — same setup as new properties get on payment.
            </p>
          </div>
        </div>
      )}
      {category && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-5 py-4 flex items-start gap-3">
          <Info size={18} className="text-indigo-600 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700 leading-relaxed flex-1">
            <p className="font-semibold text-slate-900 text-sm">{propertyName || "Property"} · {category.property_type || "Property"}</p>
            <p className="mt-0.5">
              Classified as <b className="text-indigo-700">{category.category === "hotel" ? "Hotel-family" : category.category === "vacation_rental" ? "Vacation Rental" : "Ambiguous — please pick"}</b>.
              {" "}{category.unit_count} unit{category.unit_count !== 1 ? "s" : ""} configured · {category.mapped_count} Atiti listing{category.mapped_count !== 1 ? "s" : ""} mapped.
            </p>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-display font-bold text-slate-900 text-sm mb-3">OTA Connectivity</h3>
        <div className="grid sm:grid-cols-1 gap-3">
          {providers.map((p) => {
            const acct = accounts.find((a) => a.provider === p.id && a.status !== "disconnected");
            return (
              <div key={p.id} data-testid={`provider-${p.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white flex items-center justify-center shrink-0">
                    <Plug size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-slate-900">{p.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{PROVIDER_TAGLINE}</p>
                  </div>
                  {acct
                    ? <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"><CheckCircle2 size={11} /> {acct.status}</span>
                    : <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Not connected</span>}
                </div>
                <div className="flex items-center gap-2 mt-auto">
                  {acct ? (
                    <>
                      <button data-testid={`test-${p.id}`} onClick={() => testAccount(acct.id, load)}
                        className="text-xs px-3 py-1.5 rounded-full border border-slate-300 hover:border-slate-500 flex items-center gap-1"><RefreshCw size={11} /> Re-test</button>
                      <button data-testid={`disconnect-${p.id}`} onClick={() => disconnectAccount(acct.id, load)}
                        className="text-xs px-3 py-1.5 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 flex items-center gap-1"><Trash2 size={11} /> Disconnect</button>
                    </>
                  ) : (
                    <button data-testid={`connect-${p.id}`} onClick={() => setAddOpen(p)}
                      className="btn-brand rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5"><Plus size={12} /> Enable OTA Connectivity</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {accounts.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-slate-900 text-sm mb-3">Connection status</h3>
          <div className="space-y-2">
            {accounts.map((a) => (
              <div key={a.id} data-testid={`account-${a.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">OTA Connectivity
                      <span className="ml-2 text-[10px] font-bold uppercase text-slate-500">{a.environment}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-mono">Key {a.api_key_preview || "—"} · signing secret {a.webhook_secret_set ? "set ✓" : "unset"}</p>
                    {a.last_test_result && (
                      <p className={`text-[11px] mt-1 ${a.last_test_result.ok ? "text-emerald-700" : "text-rose-700"}`}>
                        {a.last_test_result.ok
                          ? <><CheckCircle2 size={11} className="inline -mt-0.5" /> {a.last_test_result.message || "Connection verified"} · {a.last_test_result.properties_visible ?? "0"} properties visible</>
                          : <><XCircle size={11} className="inline -mt-0.5" /> {a.last_test_result.reason || "Test failed"}</>}
                      </p>
                    )}
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${a.status === "connected" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : a.status === "error" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{a.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[11px] text-slate-600 leading-relaxed flex items-start gap-2">
        <ShieldCheck size={14} className="text-slate-500 shrink-0 mt-0.5" />
        <span>Credentials are stored server-side, encrypted at rest, and never sent back to your browser after the first save. Rotate any time via the account card.</span>
      </div>

      {/* Feb 2026 — Channex certification center (host self-serve prep + Go Live full sync) */}
      <CertificationCenter accounts={accounts} pid={pid} />

      {/* Phase 12 — Sync Log (drift + inbound webhooks + outbound pushes) */}
      <SyncLogPanel pid={pid} />

      {addOpen && <AddAccountDialog provider={addOpen} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
    </div>
  );

  // Optional dialog wrapper — Property-scoped usage in `/pms/property/:pid`
  // header renders as a dialog; embedded usage skips the wrapper.
  if (onClose) {
    return (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-3 sm:p-4 md:p-6 overflow-y-auto pt-safe pb-safe pl-safe pr-safe" onClick={onClose}>
          <div className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 md:px-8 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="font-display font-bold text-slate-900">OTA Connectivity</h2>
                <p className="text-[11px] text-slate-500">Connectivity + channel synchronisation</p>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="integrations-close">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 md:p-8">{body}</div>
          </div>
        </div>
      </Portal>
    );
  }
  return body;
}

async function testAccount(aid, refresh) {
  try {
    const { data } = await api.post(`/pms/integrations/accounts/${aid}/test`);
    if (data.test_result?.ok) toast.success(data.test_result.message || "Connection verified");
    else toast.error(data.test_result?.reason || "Test failed");
    refresh();
  } catch (e) { toast.error(errMsg(e)); }
}

async function disconnectAccount(aid, refresh) {
  if (!window.confirm("Disable OTA Connectivity for this account? Your reservation history + mappings stay intact — sync just pauses until you re-enable.")) return;
  try {
    await api.delete(`/pms/integrations/accounts/${aid}`);
    toast.success("OTA Connectivity disabled");
    refresh();
  } catch (e) { toast.error(errMsg(e)); }
}

async function autoProvision(pid, refresh) {
  if (!pid) return toast.error("Open a property first.");
  const tid = toast.loading("Provisioning this property on OTA Connectivity — creating property, room types, rate plans, webhook…");
  try {
    const { data } = await api.post(`/pms/properties/${pid}/channex/auto-provision`);
    toast.dismiss(tid);
    if (data.status === "ok") {
      toast.success(`Provisioned · ${Object.keys(data.room_types || {}).length} room types · ${data.mappings_created} unit mappings · initial 500-day sync running in background`);
    } else if (data.status === "skipped" && data.reason === "already_linked") {
      toast.info("Already provisioned — no changes needed.");
    } else if (data.status === "skipped" && data.reason === "no_channex_account") {
      toast.error("Connect OTA Connectivity first (Enable OTA Connectivity above).");
    } else {
      toast.warning(`Skipped: ${data.reason || "unknown"}`);
    }
    refresh();
  } catch (e) { toast.dismiss(tid); toast.error(errMsg(e)); }
}

function AddAccountDialog({ provider, onClose, onDone }) {
  const [form, setForm] = useState({ api_key: "", webhook_secret: "", environment: "staging" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!form.api_key.trim()) return toast.error("Enter the OTA Connectivity key");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/pms/integrations/accounts", {
        provider: provider.id, api_key: form.api_key.trim(),
        webhook_secret: form.webhook_secret.trim(), environment: form.environment,
      });
      setResult(data.test_result);
      if (data.test_result?.ok) {
        toast.success("OTA Connectivity enabled");
        setTimeout(onDone, 900);
      } else {
        toast.error(data.test_result?.reason || "Connection failed");
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[90] grid place-items-center p-4 sm:p-6 overflow-y-auto pt-safe pb-safe" onClick={onClose}>
      <div data-testid="add-account-dialog" className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white flex items-center justify-center">
            <Plug size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-slate-900">Enable OTA Connectivity</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Paste the connectivity key issued by Atiti. Credentials stay server-side.</p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Environment</label>
            <div className="mt-1 inline-flex bg-slate-100 rounded-full p-1 text-[11px] font-semibold">
              {["staging", "production"].map((e) => (
                <button key={e} data-testid={`env-${e}`} onClick={() => setForm({ ...form, environment: e })}
                  className={`px-3 py-1.5 rounded-full ${form.environment === e ? "bg-[#4F46E5] text-white shadow" : "text-slate-500"}`}>
                  {e === "staging" ? "Staging (test)" : "Production (live)"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">OTA Connectivity Key *</label>
            <input data-testid="add-account-api-key" value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder="Paste your OTA Connectivity key" className={`${inp} font-mono`} autoFocus />
            <p className="text-[10px] text-slate-500 mt-1">Issued by Atiti when your connectivity account is provisioned. Keep it secret.</p>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Webhook signing secret (optional)</label>
            <input data-testid="add-account-webhook-secret" value={form.webhook_secret}
              onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
              placeholder="Leave blank if you don't have one yet" className={`${inp} font-mono`} />
          </div>
          {result && !result.ok && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-800">
              <p className="font-semibold">Test failed</p>
              <p className="mt-0.5">{result.reason || `HTTP ${result.status}`}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-slate-200 hover:border-slate-400">Cancel</button>
          <button data-testid="add-account-submit" onClick={submit} disabled={busy}
            className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Plug size={13} />} Save & verify
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// Phase 12 (2026-02): Sync log — combined inbound webhook feed + outbound
// push jobs. Property-scoped when `pid` is passed so the admin sees only
// what touched this property; empty state is the calm normal.
function SyncLogPanel({ pid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const q = pid ? `?pid=${pid}&limit=20` : "?limit=20";
    api.get(`/pms/integrations/sync-log${q}`).then(({ data }) => setRows(data))
      .catch(() => setRows({ webhooks: [], sync_jobs: [] }));
  }, [pid]);
  if (!rows) return null;
  const total = (rows.webhooks?.length || 0) + (rows.sync_jobs?.length || 0);
  return (
    <details className="bg-white rounded-2xl border border-slate-200 p-4 group" data-testid="sync-log">
      <summary className="list-none cursor-pointer flex items-center justify-between">
        <span className="font-display font-bold text-slate-900 text-sm">Sync Log</span>
        <span className="text-[11px] text-slate-500 font-mono">{total ? `${total} event(s)` : "quiet — nothing to report"}</span>
      </summary>
      {total === 0
        ? <p className="text-[11px] text-slate-400 mt-3">No inbound webhooks or outbound pushes recorded yet. This log fills up automatically as reservations flow in and rates go out.</p>
        : (
          <ul className="mt-3 space-y-1.5 text-[11px]">
            {rows.sync_jobs.map((j) => {
              const ok = j.result?.ok ?? (j.rate_result?.ok && j.availability_result?.ok);
              const label = j.kind === "ari_bulk_push" ? "bulk" : (j.kind === "restrictions_push" ? "restr" : "push");
              return (
                <li key={j.id} className="flex gap-3 items-baseline" data-testid={`sync-job-${j.id}`}>
                  <span className="text-slate-400 font-mono w-28 shrink-0">{new Date(j.created_at).toLocaleString()}</span>
                  <span className={`inline-block w-16 shrink-0 font-semibold ${ok ? "text-emerald-600" : "text-amber-600"}`}>{label}</span>
                  <span className="text-slate-600 truncate flex-1">
                    {j.count} row{j.count !== 1 ? "s" : ""}
                    {j.merged_rows > 1 && ` · merged ${j.merged_rows} saves`}
                    {j.task_id && <> · task <code className="text-[10px] text-indigo-700">{String(j.task_id).slice(0, 8)}…</code></>}
                  </span>
                </li>
              );
            })}
            {rows.webhooks.map((w) => (
              <li key={w.id} className="flex gap-3 items-baseline">
                <span className="text-slate-400 font-mono w-28 shrink-0">{new Date(w.received_at).toLocaleString()}</span>
                <span className={`inline-block w-16 shrink-0 font-semibold ${w.status === "processed" ? "text-emerald-600" : w.status === "error" ? "text-rose-600" : "text-slate-500"}`}>in · {w.event_type}</span>
                <span className="text-slate-600 truncate">{w.error || w.status}</span>
              </li>
            ))}
          </ul>
        )}
    </details>
  );
}

// Feb 2026 — Channex certification center. Two big buttons the host needs
// during the certification stages: Prepare Test Property (creates the 2×2
// room/rate-plan skeleton Channex asks for) + Run Full Sync (fires the
// 500-day 2-API-call Go-Live push cert §1 requires). Plus a copy-friendly
// list of recent task_ids to paste into the certification Google form.
function CertificationCenter({ accounts, pid }) {
  const connected = accounts.find((a) => a.status !== "disconnected");
  const [busy, setBusy] = useState("");
  const [tasks, setTasks] = useState([]);
  const [result, setResult] = useState(null);

  const loadTasks = useCallback(() => {
    api.get("/pms/integrations/certification/task-ids?limit=15")
      .then(({ data }) => setTasks(data.tasks || []))
      .catch(() => setTasks([]));
  }, []);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const prep = async () => {
    if (!connected) return toast.error("Connect OTA Connectivity first.");
    setBusy("prep");
    try {
      const { data } = await api.post(`/pms/integrations/accounts/${connected.id}/prepare-test-property`);
      setResult({ kind: "prep", ...data });
      toast.success(`Test Property ready · ${Object.keys(data.room_types || {}).length} room types · ${(data.rate_plans || []).length} rate plans`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const runFullSync = async () => {
    if (!connected) return toast.error("Connect OTA Connectivity first.");
    if (!pid) return toast.error("Open a property first to run Full Sync from that property's page.");
    if (!window.confirm("Run a 500-day Full Sync push to OTA Connectivity? This emits exactly 2 API calls (availability + rates/restrictions) and should be done at Go-Live or after long downtime, not on a schedule.")) return;
    setBusy("sync");
    try {
      const { data } = await api.post(`/pms/integrations/accounts/${connected.id}/properties/${pid}/full-sync`);
      setResult({ kind: "sync", ...data });
      toast.success(`Full Sync queued · ${data.availability_rows} avail + ${data.rate_restriction_rows} rate rows · watch Sync Log for task IDs`);
      setTimeout(loadTasks, 4000);
      setTimeout(loadTasks, 12000);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  const copyTasks = () => {
    const lines = tasks.filter((t) => t.task_id).map((t) => t.task_id).join("\n");
    if (!lines) return toast.error("No task IDs yet — trigger a rate/restriction save or a full sync first.");
    navigator.clipboard.writeText(lines);
    toast.success(`${tasks.filter((t) => t.task_id).length} task ID(s) copied — paste into Channex certification form`);
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-pink-50 rounded-2xl border border-indigo-200 p-5" data-testid="certification-center">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-slate-900 text-sm flex items-center gap-2">
            <ShieldCheck size={14} className="text-indigo-600" /> Certification Center
          </h3>
          <p className="text-[11px] text-slate-600 mt-1 max-w-xl">
            Everything you need to complete OTA Connectivity's Go-Live certification: provision the exact test property structure, run the 500-day Full Sync, and collect task IDs for the certification form.
          </p>
        </div>
        <button onClick={loadTasks} className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-1">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <button data-testid="cert-prepare-test-property" onClick={prep} disabled={!connected || busy === "prep"}
          className="text-left bg-white border border-slate-200 hover:border-indigo-400 rounded-xl p-4 disabled:opacity-50 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-display font-bold text-slate-900">1 · Prepare Test Property</span>
            {busy === "prep" ? <Loader2 size={12} className="animate-spin text-indigo-500" /> : <Plus size={12} className="text-indigo-500" />}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">Creates 'Test Property - Atiti' with USD · Twin + Double rooms · BAR + B&amp;B rate plans. Idempotent.</p>
        </button>
        <button data-testid="cert-run-full-sync" onClick={runFullSync} disabled={!connected || !pid || busy === "sync"}
          className="text-left bg-white border border-slate-200 hover:border-indigo-400 rounded-xl p-4 disabled:opacity-50 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-display font-bold text-slate-900">2 · Run 500-day Full Sync</span>
            {busy === "sync" ? <Loader2 size={12} className="animate-spin text-indigo-500" /> : <RefreshCw size={12} className="text-indigo-500" />}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            Pushes 500 days of availability + rates + restrictions in EXACTLY 2 API calls.
            {!pid && <span className="block text-amber-600 mt-1">Open a property page to enable this.</span>}
          </p>
        </button>
      </div>

      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-slate-700">3 · Task IDs for Certification Form ({tasks.filter((t) => t.task_id).length})</span>
          <button data-testid="cert-copy-task-ids" onClick={copyTasks}
            className="text-[10px] px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold">
            Copy all
          </button>
        </div>
        {tasks.length === 0 ? (
          <p className="text-[10px] text-slate-400 mt-2">No pushes yet — trigger a rate/restriction save from the Rate Change Wizard, or run Full Sync.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-[10px] font-mono">
            {tasks.slice(0, 10).map((t, i) => (
              <li key={i} className="flex gap-2 items-baseline">
                <span className="text-slate-400 w-24 shrink-0">{new Date(t.created_at).toLocaleTimeString()}</span>
                <code className={t.task_id ? "text-indigo-700" : "text-slate-400"}>{t.task_id || "— (no task id)"}</code>
                <span className="text-slate-400">{t.count} row{t.count !== 1 ? "s" : ""}{t.mode ? ` · ${t.mode}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {result && (
        <div className="mt-3 bg-white rounded-xl border border-indigo-200 p-3">
          <p className="text-[11px] font-semibold text-slate-700">Latest result</p>
          <pre className="text-[10px] font-mono text-slate-600 mt-1 whitespace-pre-wrap max-h-32 overflow-y-auto">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
